import { useContext, useState, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Form } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import type { TLoanStatus } from '../../common';
import { TLoanStatuses } from '../../common';
import { useMambuLoanStore, UserContext, useUsersStore } from '../../contexts';
import { getMerchantUsers, searchMambuLoans } from '../../contexts';
import {
  ButtonContainer,
  InputStatuses,
  InputWrapper,
  PrimaryAltButton,
  SecondaryAltButton,
  ToggleButton
} from '../atoms';
import type { TLoanFilterOptions } from '../types';

const filterFormSchema = z.object({
  applicationNumber: z.string().optional(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  includeMigrated: z.boolean().optional(),
  lastName: z.string().optional(),
  planNumber: z.string().optional(),
  salesAgent: z.string().optional(),
  statuses: z
    .array(
      z.custom<TLoanStatus>(
        (status) => (status as TLoanStatus).includes(status as string),
        `status must be one of ["${TLoanStatuses.join('", "')}"]`
      )
    )
    .optional()
});

type FilterFormSchema = z.infer<typeof filterFormSchema>;

type TFilter = {
  open: boolean;
};

type TFilterFields = {
  applicationNumber?: string;
  email?: string;
  firstName?: string;
  includeMigrated?: boolean;
  lastName?: string;
  planNumber?: string;
  salesAgent?: string;
};

export type TLoanFilter = {
  productType: string;
};

const Filter = styled(Form)<TFilter>(
  ({ open }) => css`
    margin-top: 20px;
    max-height: 0;
    overflow: hidden;
    transition: 0.35s ease;

    ${open &&
    css`
      transition: 0.5s cubic-bezier(0.79, -0.06, 1, 1);
      max-height: 1000px;
    `}
  `
);

const InputContainer = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    align-items: end;
    grid-gap: 0 ${theme.margins.md};
    border-top: ${theme.borders.light};
    border-bottom: ${theme.borders.light};
    padding-top: ${theme.paddings.md};
    padding-bottom: ${theme.paddings.sm};

    @media (max-width: ${theme.breakpoints.max.md}) {
      grid-gap: 0 1.25rem;

      label {
        font-size: 0.875rem;
      }
    }
  `
);

const StatusesWrapper = styled.div`
  grid-column: 1 / span 4;
`;

const FormButtons = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 20px;
`;

const LoanFilter = ({ productType }: TLoanFilter) => {
  const { user, userInfo } = useContext(UserContext);
  const merchantId = user.merchantId;

  let initialFilter = {};
  if (userInfo.userId && userInfo.role === 'Sales') {
    initialFilter = { salesAgentID: userInfo.userId };
  }

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [resetStatus, setResetStatus] = useState(false);
  const [filter, setFilter] = useState(initialFilter);

  const pagination = useMambuLoanStore((s) => s.searchPagination);
  const merchantUsers = useUsersStore((s) => s.merchantUsers);
  const setPagination = useMambuLoanStore((s) => s.setSearchPagination);
  const loading = useMambuLoanStore((s) => s.isLoading);
  const setLoading = useMambuLoanStore((s) => s.setLoading);
  const setSearchPagination = useMambuLoanStore((s) => s.setSearchPagination);
  const setSearchResults = useMambuLoanStore((s) => s.setSearchResults);
  // const setSearchFilter = useMambuLoanStore((s) => s.setSearchFilter);
  const setMerchantUsers = useUsersStore((s) => s.setMerchantUsers);
  const clearState = useMambuLoanStore((s) => s.clearState);

  const setFormFilter = (filter = {}) => {
    // reset pagination with filter changes
    setPagination({
      ...pagination,
      currentPage: 1
    });
    setFilter(filter);
  };

  useEffect(() => {
    clearState();
  }, []);

  // @TODO - this should be restricted on the backend
  // Sales user doesn't have permission to get all users
  if (userInfo.role !== 'Sales') {
    const { isLoading, refetch } = getMerchantUsers({
      merchantId,
      setMerchantUsers,
      query: useQuery
    });
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FilterFormSchema>({
    resolver: zodResolver(filterFormSchema),
    defaultValues: {
      applicationNumber: '',
      email: '',
      firstName: '',
      includeMigrated: false,
      lastName: '',
      planNumber: '',
      salesAgent: '',
      statuses: [],
      ...initialFilter
    } // Values don't 'reset' if undefined in react hook form
  });

  searchMambuLoans({
    merchantId,
    filter,
    pagination,
    productType,
    setLoading,
    setSearchPagination,
    setSearchResults,
    query: useQuery
  });

  const onSubmit: SubmitHandler<FilterFormSchema> = async (
    data: TFilterFields
  ) => {
    if (!loading) {
      const options: TLoanFilterOptions = {};
      if (data.planNumber) {
        options.originalAccountNumber = data.planNumber;
      }
      if (data.applicationNumber) {
        options.applicationID = data.applicationNumber;
      }
      if (data.firstName) {
        options.firstName = data.firstName;
      }
      if (data.lastName) {
        options.lastName = data.lastName;
      }
      if (data.salesAgent) {
        options.salesAgentID = data.salesAgent;
      } else if (userInfo.role === 'Sales') {
        options.salesAgentID = userInfo.userId;
      }
      if (data.email) {
        options.emailAddress = data.email;
      }
      options.includeMigrated = !!data.includeMigrated;
      if (productType === 'PAS' && data.statuses && data.statuses.length) {
        const states: TMambuStates = [];
        if (data.statuses.includes('Active - Good Standing')) {
          states.push('ACTIVE');
        }
        if (data.statuses.includes('Active - Bad Standing')) {
          states.push('ACTIVE_IN_ARREARS');
        }
        if (data.statuses.includes('Approved - Not signed')) {
          states.push('PENDING_APPROVAL');
        }
        if (data.statuses.includes('Approved')) {
          states.push('APPROVED');
        }
        if (data.statuses.includes('Closed')) {
          states.push('CLOSED');
        }
        if (states.length) {
          options.accountStates = states;
        }
      }
      setFormFilter(options);
    }
  };

  const handleReset = () => {
    if (!loading) {
      reset();
      setFormFilter(initialFilter);
    }
  };

  return (
    <div>
      <ToggleButton
        alternative={isFilterOpen}
        onClick={() => setIsFilterOpen(!isFilterOpen)}
      >
        {isFilterOpen ? 'Close' : 'Open'} Filters
      </ToggleButton>
      <Filter
        open={isFilterOpen}
        onSubmit={handleSubmit(onSubmit)}
        onReset={handleReset}
      >
        <InputContainer>
          <InputWrapper
            name="planNumber"
            type="text"
            label="Plan Number"
            register={register}
            errorMessage={errors.planNumber?.message}
          />
          <InputWrapper
            name="applicationNumber"
            type="text"
            label="Application Number"
            register={register}
            errorMessage={errors.applicationNumber?.message}
          />
          <InputWrapper
            name="firstName"
            type="text"
            label="First Name"
            register={register}
            errorMessage={errors.firstName?.message}
          />
          <InputWrapper
            name="lastName"
            type="text"
            label="Last Name"
            register={register}
            errorMessage={errors.lastName?.message}
          />
          <InputWrapper
            name="email"
            type="text"
            label="Email"
            register={register}
            errorMessage={errors.email?.message}
          />
          {userInfo.role !== 'Sales' && (
            <InputWrapper
              label="Sales Agent"
              name="salesAgent"
              type="select"
              complexOptions={merchantUsers?.map(({ id, name }) => ({
                value: id,
                text: name
              }))}
              register={register}
              errorMessage={errors.salesAgent?.message}
            />
          )}
          <InputWrapper
            label="Include Migrated Products"
            name="includeMigrated"
            type="boolean"
            errorMessage={undefined}
            register={register}
          />
          {productType === 'PAS' && (
            <StatusesWrapper>
              <InputStatuses
                type="loan"
                reset={resetStatus}
                name="statuses"
                register={register}
              />
            </StatusesWrapper>
          )}
        </InputContainer>
        <FormButtons
          horizontalSpacing="6px"
          verticalSpacing="0"
          justify="flex-end"
        >
          <PrimaryAltButton type="submit" isLoading={loading}>
            Apply Filters
          </PrimaryAltButton>
          <SecondaryAltButton
            type="reset"
            onClick={() => setResetStatus(!resetStatus)}
            isLoading={loading}
          >
            Reset Filters
          </SecondaryAltButton>
        </FormButtons>
      </Filter>
    </div>
  );
};

export { LoanFilter };
